// /app/frontend/src/pages/admin/SystemSettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { clientsAPI, moduleTemplatesAPI } from '../../services/api';
import {
  Settings, Package, Building2, Check, X, Search, Shield,
  ClipboardCheck, ShieldCheck, Activity, Loader2, Save,
  Database, Network, FileWarning, UserCheck, Briefcase, Target,
  FileSearch, ClipboardList, CheckSquare, AlertTriangle, FileText, FolderOpen,
  Key
} from 'lucide-react';

// Module configuration with details
const MODULE_CATALOG = {
  // Compliance
  maturity: { 
    name: 'Maturity Assessment', 
    description: 'TR2UST maturity assessment framework',
    icon: Target, 
    category: 'compliance',
    tier: 'enterprise'
  },
  ropa: { 
    name: 'ROPA', 
    description: 'Record of Processing Activities management',
    icon: Database, 
    category: 'compliance',
    tier: 'starter'
  },
  data_flow: { 
    name: 'Data Flow Map', 
    description: 'Visual data flow mapping',
    icon: Network, 
    category: 'compliance',
    tier: 'starter'
  },
  breach: { 
    name: 'Data Breach', 
    description: 'Breach incident management and reporting',
    icon: FileWarning, 
    category: 'compliance',
    tier: 'starter'
  },
  dsr: { 
    name: 'DSR Management', 
    description: 'Data Subject Request handling',
    icon: UserCheck, 
    category: 'compliance',
    tier: 'starter'
  },
  vendor: { 
    name: 'Vendor Management', 
    description: 'Third-party vendor risk management',
    icon: Briefcase, 
    category: 'compliance',
    tier: 'sme'
  },
  // Assessments
  due_diligence: { 
    name: 'Due Diligence', 
    description: 'Vendor due diligence assessments',
    icon: FileSearch, 
    category: 'assessments',
    tier: 'enterprise'
  },
  staff_assessment: { 
    name: 'Staff Assessments', 
    description: 'Staff awareness and training questionnaires',
    icon: ClipboardList, 
    category: 'assessments',
    tier: 'enterprise'
  },
  readiness: { 
    name: 'Readiness Assessment', 
    description: 'Compliance readiness evaluations',
    icon: CheckSquare, 
    category: 'assessments',
    tier: 'sme'
  },
  // Audit & Assurance
  risk_assessment: { 
    name: 'Risk Assessments', 
    description: 'DPIA, AI assessments, and risk analysis',
    icon: AlertTriangle, 
    category: 'audit',
    tier: 'sme'
  },
  risk_register: { 
    name: 'Risk Register', 
    description: 'Centralized risk tracking and management',
    icon: FileText, 
    category: 'audit',
    tier: 'sme'
  },
  // Operations
  tasks: { 
    name: 'Tasks', 
    description: 'Task management and tracking',
    icon: ClipboardList, 
    category: 'operations',
    tier: 'starter'
  },
  documents: { 
    name: 'Documents', 
    description: 'Document management system',
    icon: FolderOpen, 
    category: 'operations',
    tier: 'starter'
  },
};

// Category configuration
const CATEGORIES = {
  compliance: { name: 'Compliance', icon: Shield, color: 'text-blue-500' },
  assessments: { name: 'Assessments', icon: ClipboardCheck, color: 'text-purple-500' },
  audit: { name: 'Audit & Assurance', icon: ShieldCheck, color: 'text-green-500' },
  operations: { name: 'Operations', icon: Activity, color: 'text-orange-500' },
};

// Tier configuration
const TIERS = {
  starter: { 
    name: 'Starter', 
    description: 'Core compliance essentials',
    color: 'bg-blue-500',
    modules: ['ropa', 'data_flow', 'breach', 'dsr', 'tasks', 'documents']
  },
  sme: { 
    name: 'SME', 
    description: 'Enhanced features for growing businesses',
    color: 'bg-purple-500',
    modules: ['ropa', 'data_flow', 'breach', 'dsr', 'vendor', 'readiness', 'risk_assessment', 'risk_register', 'tasks', 'documents']
  },
  enterprise: { 
    name: 'Enterprise', 
    description: 'Full suite with advanced features',
    color: 'bg-green-500',
    modules: Object.keys(MODULE_CATALOG)
  },
  // Legacy tier mappings for backward compatibility
  basic: { 
    name: 'Starter (Legacy)', 
    description: 'Core compliance essentials',
    color: 'bg-blue-500',
    modules: ['ropa', 'data_flow', 'breach', 'dsr', 'tasks', 'documents']
  },
  professional: { 
    name: 'SME (Legacy)', 
    description: 'Enhanced features for growing businesses',
    color: 'bg-purple-500',
    modules: ['ropa', 'data_flow', 'breach', 'dsr', 'vendor', 'readiness', 'risk_assessment', 'risk_register', 'tasks', 'documents']
  },
};

// Normalize tier name (convert legacy to new)
const normalizeTier = (tier) => {
  const tierMap = {
    'basic': 'starter',
    'professional': 'sme',
  };
  return tierMap[tier] || tier || 'enterprise';
};

function ModuleCard({ moduleId, module, isEnabled, isDefault, onToggle, disabled }) {
  // Per "Fluid Module Assignment" spec: every module is individually toggleable
  // per client. The "Tier Default" badge is purely informational — disabling a
  // tier-default module is allowed (no lock).
  const Icon = module.icon;
  const tierBadge = TIERS[module.tier] || TIERS.enterprise;

  return (
    <div className={`p-4 rounded-lg border transition-all ${
      isEnabled ? 'bg-card border-primary/50' : 'bg-muted/30 border-border'
    } ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
            <Icon className={`h-5 w-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-sm">{module.name}</h4>
              <Badge variant="outline" className={`text-xs ${tierBadge.color} text-white border-none`}>
                {tierBadge.name}
              </Badge>
              {isDefault && (
                <Badge variant="secondary" className="text-xs" title="Included in this tier's template by default">In template</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{module.description}</p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={() => onToggle(moduleId)}
          disabled={disabled}
          className="flex-shrink-0"
        />
      </div>
    </div>
  );
}

export default function SystemSettingsPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientModules, setClientModules] = useState({});
  const [clientTier, setClientTier] = useState('enterprise');

  // Task 5.4B: Seat Limits tab state
  const [seatClientId, setSeatClientId] = useState('');
  const [seatInfo, setSeatInfo] = useState(null);
  const [overrideValue, setOverrideValue] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  // Task 6: MFA Policy tab state
  const [mfaClientId, setMfaClientId] = useState('');
  const [currentMfaPolicy, setCurrentMfaPolicy] = useState('optional');
  const [selectedMfaPolicy, setSelectedMfaPolicy] = useState('optional');
  const [savingMfaPolicy, setSavingMfaPolicy] = useState(false);
  const [loadingMfaPolicy, setLoadingMfaPolicy] = useState(false);

  // Tier Templates tab state (DB-backed per "Fluid Module Assignment" spec).
  // Source of truth for "what does Starter include" — editable at runtime by SA.
  // Initialised from the static TIERS fallback so the page renders before fetch.
  const [tierTemplates, setTierTemplates] = useState({
    starter: TIERS.starter.modules,
    sme: TIERS.sme.modules,
    enterprise: TIERS.enterprise.modules,
  });
  const [savingTier, setSavingTier] = useState(null); // tier currently being saved (or null)

  // Helper: read the dynamic modules for a tier (replaces TIERS[x].modules everywhere).
  // Falls back to the static TIERS constant if templates haven't loaded yet.
  const getTierModulesFromState = (tier) => {
    const normalized = normalizeTier(tier);
    return tierTemplates[normalized] || TIERS[normalized]?.modules || TIERS.enterprise.modules;
  };

  useEffect(() => {
    loadClients();
    loadTierTemplates();
  }, []);

  const loadTierTemplates = async () => {
    try {
      const res = await moduleTemplatesAPI.getAll();
      const next = { starter: [], sme: [], enterprise: [] };
      (res.data || []).forEach((t) => {
        if (t.tier && next.hasOwnProperty(t.tier)) {
          next[t.tier] = t.enabled_modules || [];
        }
      });
      setTierTemplates(next);
    } catch (err) {
      // Non-fatal — fall back to the static TIERS constant
      console.warn('Failed to load tier templates, using static defaults:', err);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    const normalizedTier = normalizeTier(client?.subscription_tier);
    setClientTier(normalizedTier);

    // Per "Fluid Module Assignment" spec: client.enabled_modules is the source
    // of truth. Tier defaults are only used at client creation; once the client
    // exists, modules are independent of tier.
    const enabled = Array.isArray(client?.enabled_modules) && client.enabled_modules.length > 0
      ? client.enabled_modules
      : getTierModulesFromState(normalizedTier);  // legacy clients with no enabled_modules

    const moduleState = {};
    Object.keys(MODULE_CATALOG).forEach(moduleId => {
      moduleState[moduleId] = enabled.includes(moduleId);
    });
    setClientModules(moduleState);
  };

  const handleTierChange = (newTier) => {
    // Per spec: tier is just a label. Changing it does NOT touch enabled_modules.
    // Use the "Reset modules to template" action below if SA wants that.
    setClientTier(newTier);
  };

  const handleModuleToggle = (moduleId) => {
    // Per spec: every module is individually toggleable per client. No "tier
    // default" lock — SA can disable any module on any client regardless of tier.
    setClientModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleResetToTemplate = async () => {
    if (!selectedClient) return;
    if (!window.confirm(`Reset ${selectedClient.client_name}'s modules to the current ${clientTier} template? This overwrites the client's current module list.`)) return;
    setSaving(true);
    try {
      const res = await clientsAPI.applyTierTemplate(selectedClient.id);
      const enabled = res.data?.enabled_modules || getTierModulesFromState(clientTier);
      const moduleState = {};
      Object.keys(MODULE_CATALOG).forEach(moduleId => {
        moduleState[moduleId] = enabled.includes(moduleId);
      });
      setClientModules(moduleState);
      // Update the client list cache too
      setClients(prev => prev.map(c => c.id === selectedClient.id ? { ...c, enabled_modules: enabled } : c));
      setSelectedClient(prev => ({ ...prev, enabled_modules: enabled }));
      toast.success(`Modules reset from ${clientTier} template`);
    } catch (err) {
      console.error('Reset failed:', err);
      toast.error('Failed to reset modules from template');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedClient) return;

    setSaving(true);
    try {
      const enabledModules = Object.keys(clientModules).filter(m => clientModules[m]);

      // Per "Fluid Module Assignment" spec: tier is a label; changing it does
      // NOT auto-reset modules. We persist the tier label and the explicit
      // enabled_modules list separately. `module_overrides` is no longer
      // written — `enabled_modules` is the only source of truth.
      const currentTier = normalizeTier(selectedClient?.subscription_tier);
      if (clientTier !== currentTier) {
        await clientsAPI.updateSubscription(selectedClient.id, clientTier);
      }
      await clientsAPI.updateModules(selectedClient.id, enabledModules);

      setClients(prev => prev.map(c =>
        c.id === selectedClient.id
          ? { ...c, subscription_tier: clientTier, enabled_modules: enabledModules }
          : c
      ));
      setSelectedClient(prev => ({ ...prev, subscription_tier: clientTier, enabled_modules: enabledModules }));

      toast.success('Module configuration saved');
    } catch (error) {
      console.error('Failed to save configuration:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const filteredClients = clients.filter(client =>
    client.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTierModuleCount = (tier) => {
    return getTierModulesFromState(tier).length;
  };

  const getEnabledCount = () => {
    return Object.values(clientModules).filter(Boolean).length;
  };

  // "Override count" = how many enabled modules differ from the current tier template.
  // Purely informational — there's no longer a hard rule that locks tier defaults.
  const getOverrideCount = () => {
    if (!selectedClient) return 0;
    const templateModules = getTierModulesFromState(clientTier);
    return Object.keys(clientModules).filter(
      moduleId => clientModules[moduleId] && !templateModules.includes(moduleId)
    ).length;
  };

  // Tier Templates tab — toggle a module in/out of a tier's template (SA only).
  const toggleTierModule = (tier, moduleId) => {
    setTierTemplates(prev => {
      const current = new Set(prev[tier] || []);
      if (current.has(moduleId)) current.delete(moduleId); else current.add(moduleId);
      return { ...prev, [tier]: Array.from(current) };
    });
  };

  const saveTierTemplate = async (tier) => {
    setSavingTier(tier);
    try {
      await moduleTemplatesAPI.update(tier, tierTemplates[tier] || []);
      toast.success(`${TIERS[tier]?.name || tier} template saved`);
    } catch (err) {
      console.error('Save tier template failed:', err);
      toast.error('Failed to save tier template');
    } finally {
      setSavingTier(null);
    }
  };

  // Task 5.4B: Seat override handlers
  const handleSeatClientSelect = async (clientId) => {
    setSeatClientId(clientId);
    try {
      const response = await clientsAPI.getSeats(clientId);
      setSeatInfo(response.data);
      setOverrideValue(response.data.seat_limit_override?.toString() || '');
      setOverrideReason(response.data.seat_limit_override_reason || '');
    } catch (error) {
      console.error('Failed to load seat info:', error);
      toast.error('Failed to load seat information');
    }
  };

  const handleSeatOverrideSave = async () => {
    if (!seatClientId) return;

    setSavingOverride(true);
    try {
      const overrideData = {
        seat_limit_override: overrideValue ? parseInt(overrideValue) : null,
        seat_limit_override_reason: overrideReason || null
      };

      await clientsAPI.overrideSeats(seatClientId, overrideData);

      // Update seat info display
      await handleSeatClientSelect(seatClientId);

      toast.success('Seat limit override saved successfully');
    } catch (error) {
      console.error('Failed to save override:', error);
      toast.error('Failed to save seat override');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleClearOverride = async () => {
    if (!seatClientId) return;

    setSavingOverride(true);
    try {
      await clientsAPI.overrideSeats(seatClientId, {
        seat_limit_override: null,
        seat_limit_override_reason: null
      });

      // Update display
      await handleSeatClientSelect(seatClientId);

      toast.success('Seat limit override cleared');
    } catch (error) {
      console.error('Failed to clear override:', error);
      toast.error('Failed to clear seat override');
    } finally {
      setSavingOverride(false);
    }
  };

  // Task 6: MFA Policy handlers
  const handleMfaClientSelect = async (clientId) => {
    setMfaClientId(clientId);
    setLoadingMfaPolicy(true);
    try {
      const response = await clientsAPI.getMfaPolicy(clientId);
      const policy = response.data.mfa_policy || 'optional';
      setCurrentMfaPolicy(policy);
      setSelectedMfaPolicy(policy);
    } catch (error) {
      console.error('Failed to load MFA policy:', error);
      toast.error('Failed to load MFA policy');
      setCurrentMfaPolicy('optional');
      setSelectedMfaPolicy('optional');
    } finally {
      setLoadingMfaPolicy(false);
    }
  };

  const handleMfaPolicySave = async () => {
    if (!mfaClientId) return;

    setSavingMfaPolicy(true);
    try {
      await clientsAPI.setMfaPolicy(mfaClientId, {
        mfa_policy: selectedMfaPolicy
      });

      setCurrentMfaPolicy(selectedMfaPolicy);
      toast.success('MFA policy updated successfully');
    } catch (error) {
      console.error('Failed to save MFA policy:', error);
      toast.error('Failed to update MFA policy');
    } finally {
      setSavingMfaPolicy(false);
    }
  };

  // Get display tier info (handles legacy tiers)
  const getTierInfo = (tier) => {
    const normalizedTier = normalizeTier(tier);
    return TIERS[normalizedTier] || TIERS.enterprise;
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="system-settings-page">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            System Settings
          </h1>
          <p className="text-muted-foreground">
            Manage subscription tiers and module access for clients
          </p>
        </div>

        <Tabs defaultValue="modules" className="space-y-6">
          <TabsList>
            <TabsTrigger value="modules" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Module Configuration
            </TabsTrigger>
            <TabsTrigger value="seats" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Seat Limits
            </TabsTrigger>
            <TabsTrigger value="mfa" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              MFA Policy
            </TabsTrigger>
            <TabsTrigger value="tiers" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Tier Defaults
            </TabsTrigger>
          </TabsList>

          {/* Module Configuration Tab */}
          <TabsContent value="modules" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Client Selector */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Select Client
                  </CardTitle>
                  <CardDescription>
                    Choose a client to configure their module access
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search clients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2 pr-4">
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : filteredClients.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No clients found</p>
                      ) : (
                        filteredClients.map(client => {
                          const tierInfo = getTierInfo(client.subscription_tier);
                          return (
                          <button
                            key={client.id}
                            onClick={() => handleClientSelect(client.id)}
                            className={`w-full text-left p-3 rounded-lg border transition-all ${
                              selectedClient?.id === client.id
                                ? 'bg-primary/10 border-primary'
                                : 'bg-card hover:bg-muted/50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm">{client.client_name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className={`text-xs ${tierInfo.color} text-white border-none`}>
                                    {tierInfo.name}
                                  </Badge>
                                  {client.module_overrides?.length > 0 && (
                                    <Badge variant="secondary" className="text-xs">
                                      +{client.module_overrides.length} override{client.module_overrides.length > 1 ? 's' : ''}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {selectedClient?.id === client.id && (
                                <Check className="h-4 w-4 text-primary" />
                              )}
                            </div>
                          </button>
                        );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Module Configuration */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {selectedClient ? selectedClient.client_name : 'Module Access'}
                      </CardTitle>
                      <CardDescription>
                        {selectedClient 
                          ? `Configure module access for this client`
                          : 'Select a client to configure their modules'}
                      </CardDescription>
                    </div>
                    {selectedClient && (
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium">{getEnabledCount()} / {Object.keys(MODULE_CATALOG).length}</p>
                          <p className="text-xs text-muted-foreground">modules enabled</p>
                        </div>
                        {getOverrideCount() > 0 && (
                          <Badge className="bg-purple-500">
                            +{getOverrideCount()} override{getOverrideCount() > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!selectedClient ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Building2 className="h-12 w-12 mb-4 opacity-50" />
                      <p>Select a client from the list to configure their modules</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Tier Selector */}
                      <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                        <Label className="font-medium">Subscription Tier:</Label>
                        <Select value={clientTier} onValueChange={handleTierChange}>
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(TIERS).map(([key, tier]) => (
                              <SelectItem key={key} value={key}>
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${tier.color}`} />
                                  {tier.name} ({getTierModuleCount(key)} modules)
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground">
                          {TIERS[clientTier]?.description || ''}
                        </p>
                      </div>

                      {/* Module Grid by Category */}
                      <ScrollArea className="h-[400px]">
                        <div className="space-y-6 pr-4">
                          {Object.entries(CATEGORIES).map(([categoryId, category]) => {
                            const CategoryIcon = category.icon;
                            const categoryModules = Object.entries(MODULE_CATALOG).filter(
                              ([_, module]) => module.category === categoryId
                            );
                            
                            return (
                              <div key={categoryId}>
                                <h3 className={`font-medium flex items-center gap-2 mb-3 ${category.color}`}>
                                  <CategoryIcon className="h-4 w-4" />
                                  {category.name}
                                </h3>
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                                  {categoryModules.map(([moduleId, module]) => {
                                    const isDefault = TIERS[clientTier]?.modules?.includes(moduleId) || false;
                                    return (
                                      <ModuleCard
                                        key={moduleId}
                                        moduleId={moduleId}
                                        module={module}
                                        isEnabled={clientModules[moduleId]}
                                        isDefault={isDefault}
                                        onToggle={handleModuleToggle}
                                        disabled={saving}
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </CardContent>
                {selectedClient && (
                  <CardFooter className="border-t pt-4">
                    <div className="flex items-center justify-between w-full gap-3 flex-wrap">
                      <p className="text-sm text-muted-foreground">
                        Changes will take effect after the user logs in again
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={handleResetToTemplate}
                          disabled={saving}
                          title={`Overwrite this client's modules with the current ${clientTier} tier template`}
                        >
                          Reset to {clientTier} template
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Configuration
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardFooter>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Task 6: MFA Policy Tab */}
          <TabsContent value="mfa" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Client Selector */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Select Client
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search clients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1"
                      />
                      <Search className="h-4 w-4 text-muted-foreground mt-3" />
                    </div>
                    <ScrollArea className="h-[300px] pr-4">
                      <div className="space-y-1">
                        {clients
                          .filter(c =>
                            c.client_name.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map(client => (
                            <button
                              key={client.id}
                              onClick={() => handleMfaClientSelect(client.id)}
                              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                mfaClientId === client.id
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-muted'
                              }`}
                            >
                              <div className="font-medium">{client.client_name}</div>
                              <div className="text-xs opacity-70">{client.subscription_tier}</div>
                            </button>
                          ))}
                      </div>
                    </ScrollArea>
                  </div>
                </CardContent>
              </Card>

              {/* MFA Policy Configuration */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {mfaClientId ? (
                          clients.find(c => c.id === mfaClientId)?.client_name || 'MFA Policy'
                        ) : (
                          'MFA Policy'
                        )}
                      </CardTitle>
                      <CardDescription>
                        {mfaClientId
                          ? 'Configure MFA enforcement for this client'
                          : 'Select a client to configure their MFA policy'}
                      </CardDescription>
                    </div>
                    {mfaClientId && (
                      <Badge variant="outline" className="ml-2">
                        {currentMfaPolicy === 'enforced'
                          ? 'Enforced'
                          : currentMfaPolicy === 'optional'
                          ? 'Optional'
                          : 'Disabled'}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!mfaClientId ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Key className="h-12 w-12 mb-4 opacity-50" />
                      <p>Select a client from the list to configure their MFA policy</p>
                    </div>
                  ) : loadingMfaPolicy ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Current Policy Info */}
                      {currentMfaPolicy && (
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm font-medium mb-2">Current Policy</p>
                          <Badge className={`
                            ${currentMfaPolicy === 'enforced' ? 'bg-red-500' : ''}
                            ${currentMfaPolicy === 'optional' ? 'bg-blue-500' : ''}
                            ${currentMfaPolicy === 'disabled' ? 'bg-gray-500' : ''}
                          `}>
                            {currentMfaPolicy === 'enforced' && 'Enforced - Users must set up MFA'}
                            {currentMfaPolicy === 'optional' && 'Optional - Users can use MFA'}
                            {currentMfaPolicy === 'disabled' && 'Disabled - MFA feature hidden'}
                          </Badge>
                        </div>
                      )}

                      {/* Policy Selection */}
                      <div className="space-y-4">
                        <Label className="text-base font-semibold">Select MFA Policy</Label>

                        {/* Enforced Option */}
                        <div
                          onClick={() => setSelectedMfaPolicy('enforced')}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedMfaPolicy === 'enforced'
                              ? 'border-primary bg-primary/5'
                              : 'border-muted hover:border-muted-foreground'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center ${
                              selectedMfaPolicy === 'enforced'
                                ? 'border-primary bg-primary'
                                : 'border-muted-foreground'
                            }`}>
                              {selectedMfaPolicy === 'enforced' && (
                                <Check className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-sm">Enforced</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Users must set up MFA before accessing the system. Access is blocked until MFA is configured.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Optional Option */}
                        <div
                          onClick={() => setSelectedMfaPolicy('optional')}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedMfaPolicy === 'optional'
                              ? 'border-primary bg-primary/5'
                              : 'border-muted hover:border-muted-foreground'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center ${
                              selectedMfaPolicy === 'optional'
                                ? 'border-primary bg-primary'
                                : 'border-muted-foreground'
                            }`}>
                              {selectedMfaPolicy === 'optional' && (
                                <Check className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-sm">Optional</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Users can optionally enable MFA. The setup option is available but not required.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Disabled Option */}
                        <div
                          onClick={() => setSelectedMfaPolicy('disabled')}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedMfaPolicy === 'disabled'
                              ? 'border-primary bg-primary/5'
                              : 'border-muted hover:border-muted-foreground'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center ${
                              selectedMfaPolicy === 'disabled'
                                ? 'border-primary bg-primary'
                                : 'border-muted-foreground'
                            }`}>
                              {selectedMfaPolicy === 'disabled' && (
                                <Check className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-sm">Disabled</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                MFA feature is completely hidden from users. No MFA options are shown.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Save Button */}
                      {selectedMfaPolicy !== currentMfaPolicy && (
                        <Button
                          onClick={handleMfaPolicySave}
                          disabled={savingMfaPolicy}
                          className="w-full"
                        >
                          {savingMfaPolicy ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Policy
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tier Templates Tab — editable per "Fluid Module Assignment" spec.
              SA edits which modules each tier includes by default. Edits affect
              FUTURE clients created with that tier; existing clients keep their
              own enabled_modules (use "Reset to template" on a client to apply). */}
          <TabsContent value="tiers" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Tier Templates</CardTitle>
                <CardDescription>
                  These templates are applied at <strong>client creation</strong>. Editing a
                  tier here doesn't change any existing client's modules — go to the Module
                  Configuration tab and use "Reset to template" if you want to push a tier
                  edit to existing clients of that tier.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {['starter', 'sme', 'enterprise'].map((tierId) => {
                const tier = TIERS[tierId];
                const modulesInTier = tierTemplates[tierId] || [];
                return (
                  <Card key={tierId} className="relative overflow-hidden">
                    <div className={`absolute top-0 left-0 right-0 h-1 ${tier.color}`} />
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${tier.color}`} />
                          {tier.name}
                        </CardTitle>
                        <Badge variant="secondary">{modulesInTier.length} / {Object.keys(MODULE_CATALOG).length}</Badge>
                      </div>
                      <CardDescription>{tier.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(CATEGORIES).map(([categoryId, category]) => {
                        const CategoryIcon = category.icon;
                        const inCategory = Object.entries(MODULE_CATALOG)
                          .filter(([_, m]) => m.category === categoryId)
                          .map(([id]) => id);
                        if (inCategory.length === 0) return null;
                        return (
                          <div key={categoryId} className="space-y-1.5">
                            <p className={`text-xs font-medium flex items-center gap-1 ${category.color}`}>
                              <CategoryIcon className="h-3 w-3" />
                              {category.name}
                            </p>
                            <div className="space-y-1">
                              {inCategory.map(moduleId => {
                                const module = MODULE_CATALOG[moduleId];
                                const enabled = modulesInTier.includes(moduleId);
                                return (
                                  <div key={moduleId} className="flex items-center justify-between gap-2 py-1 pl-4">
                                    <span className={`text-sm ${enabled ? '' : 'text-muted-foreground'}`}>
                                      {module.name}
                                    </span>
                                    <Switch
                                      checked={enabled}
                                      onCheckedChange={() => toggleTierModule(tierId, moduleId)}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                    <CardFooter>
                      <Button
                        onClick={() => saveTierTemplate(tierId)}
                        disabled={savingTier === tierId}
                        className="w-full"
                      >
                        {savingTier === tierId ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                        ) : (
                          <><Save className="h-4 w-4 mr-2" />Save {tier.name} template</>
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Module Catalog</CardTitle>
                <CardDescription>
                  Complete list of all available modules and their tier requirements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Object.entries(MODULE_CATALOG).map(([moduleId, module]) => {
                    const Icon = module.icon;
                    const category = CATEGORIES[module.category];
                    const tier = TIERS[module.tier] || TIERS.enterprise;
                    
                    return (
                      <div key={moduleId} className="p-3 rounded-lg border bg-card">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg bg-muted`}>
                            <Icon className={`h-4 w-4 ${category.color}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-sm">{module.name}</h4>
                            <p className="text-xs text-muted-foreground mt-0.5">{module.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className={`text-xs ${tier.color} text-white border-none`}>
                                {tier.name}+
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Task 5.4B: Seat Limits Tab */}
          <TabsContent value="seats" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Client Selector */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Select Client
                  </CardTitle>
                  <CardDescription>
                    Choose a client to manage seat limits
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search clients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <ScrollArea className="h-[300px] pr-4">
                    <div className="space-y-2">
                      {filteredClients.map((client) => (
                        <button
                          key={client.id}
                          onClick={() => handleSeatClientSelect(client.id)}
                          className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                            seatClientId === client.id
                              ? 'bg-blue-100 text-blue-900 border border-blue-300'
                              : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <div className="font-medium text-sm">{client.client_name}</div>
                          <div className="text-xs text-muted-foreground">{client.subscription_tier}</div>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Seat Info & Override Form */}
              <Card className="lg:col-span-2 space-y-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5" />
                    Seat Limit Management
                  </CardTitle>
                  <CardDescription>
                    Configure seat limits for the selected client
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {seatInfo ? (
                    <>
                      {/* Current Seat Info Card */}
                      <Card className="border-2 border-muted">
                        <CardContent className="pt-6 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Subscription Tier</p>
                              <Badge variant="outline">{seatInfo.subscription_tier}</Badge>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Current Usage</p>
                              <p className="font-bold text-lg">
                                {seatInfo.user_count} / {seatInfo.seat_limit_override || seatInfo.seat_limit}
                              </p>
                            </div>
                          </div>

                          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                seatInfo.percent >= 100 ? 'bg-red-500' :
                                seatInfo.percent >= 80 ? 'bg-amber-500' :
                                'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(seatInfo.percent, 100)}%` }}
                            />
                          </div>

                          {seatInfo.seat_limit_override && (
                            <div className="p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                              <p className="font-medium text-blue-900">Override Active</p>
                              <p className="text-blue-800 text-xs mt-1">
                                Max: {seatInfo.seat_limit_override}
                              </p>
                              {seatInfo.seat_limit_override_reason && (
                                <p className="text-blue-700 text-xs mt-2">
                                  Reason: {seatInfo.seat_limit_override_reason}
                                </p>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Override Form */}
                      <div className="space-y-4 pt-4 border-t">
                        <div>
                          <Label className="text-sm font-medium">Seat Limit Override (Optional)</Label>
                          <p className="text-xs text-muted-foreground mb-2">Leave blank to use tier default ({seatInfo.seat_limit})</p>
                          <Input
                            type="number"
                            min="1"
                            value={overrideValue}
                            onChange={(e) => setOverrideValue(e.target.value)}
                            placeholder={`Default: ${seatInfo.seat_limit}`}
                            className="mb-4"
                          />
                        </div>

                        <div>
                          <Label className="text-sm font-medium">Reason (Optional)</Label>
                          <Textarea
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            placeholder="e.g., Enterprise customer, custom agreement"
                          />
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={handleSeatOverrideSave}
                            disabled={savingOverride}
                            className="flex-1"
                          >
                            {savingOverride ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Override
                              </>
                            )}
                          </Button>

                          {seatInfo.seat_limit_override && (
                            <Button
                              onClick={handleClearOverride}
                              disabled={savingOverride}
                              variant="outline"
                            >
                              {savingOverride ? 'Clearing...' : 'Clear Override'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <ShieldCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Select a client to manage seat limits</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
